"use client";
import { MouseEvent, useState } from "react";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import Image from "next/image";
import { useParams } from "next/navigation";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { Button } from "@/src/components/Button";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import {
  ApiCallsAutoSearchVehicleSelectModalData,
  MtsbuVehicleCandidate,
} from "../../types";
import { useStepsNavigation } from "../../useStepsNavigation";
import { apiCallsModalStore } from "../../modalStore";
import { isStopCallsError } from "../../sideEffects";
import { useConfirmMtsbuVehicle } from "../../useConfirmMtsbuVehicle";
import { useAnalytics } from "@/src/components/pages/StepPage/components/useAnalytics";
import { maskFunctions } from "@/src/shared/utils/mask";
import { toKebabCase, useFieldsReset, mtsbumvsFields } from "./Success";

const getBrandModelLabel = (candidate: MtsbuVehicleCandidate) => {
  if (candidate.brandModel) return candidate.brandModel;
  return [candidate.brand?.label, candidate.model?.label]
    .filter(Boolean)
    .join(" ");
};

const getBrandModelYearLabel = (candidate: MtsbuVehicleCandidate) => {
  const brandModel = getBrandModelLabel(candidate);
  return [brandModel, candidate.constructionYear].filter(Boolean).join(", ");
};

const VehicleCandidateCard = ({
  candidate,
  plateNumber,
  selected,
  onSelect,
}: {
  candidate: MtsbuVehicleCandidate;
  plateNumber?: string;
  selected: boolean;
  onSelect: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const brandLabel = candidate.brand?.label;
  const [logoSrc, setLogoSrc] = useState(
    `/vehicle-logos/${toKebabCase(brandLabel || "")}.png`,
  );

  const toggleExpand = (e: MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border-2 border-transparent bg-super-light-blue p-4 text-left transition-colors",
        { "border-blue": selected },
      )}
    >
      {brandLabel && (
        <Box className="relative flex aspect-square w-[48px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-1">
          <Image
            src={logoSrc}
            alt={brandLabel}
            width={48}
            height={48}
            quality={60}
            objectFit={"contain"}
            onError={() => setLogoSrc("/vehicle-logos/default-car.png")}
          />
        </Box>
      )}

      <Stack className="flex flex-1 flex-col gap-2">
        <Box className="text-lg font-semibold text-dark-blue">
          {getBrandModelYearLabel(candidate) || "Транспортний засіб"}
        </Box>
        {plateNumber && (
          <Box className="flex items-center gap-2">
            <Box className="flex h-[24px] w-[27px] shrink-0 rounded-l">
              <WebShopIcon name="uaFlag" />
            </Box>
            <Box className="text-common text-dark-blue">
              {maskFunctions.numberPlate(plateNumber)}
            </Box>
          </Box>
        )}
        <Box
          component="span"
          role="button"
          onClick={toggleExpand}
          className="flex items-center gap-1 self-start text-sm text-blue"
        >
          <span className="text-xs uppercase">
            {expanded ? "Показати менше" : "Показати більше"}
          </span>
          <WebShopIcon
            name="arrowDown"
            width={12}
            height={12}
            className={cn("text-blue transition-transform", {
              "rotate-180": expanded,
            })}
          />
        </Box>

        <Collapse in={expanded}>
          <Stack className="flex flex-col gap-1 pt-1 text-common text-dark-blue">
            <Box>VIN: {candidate.vin || "—"}</Box>
            <Box>
              Повна маса:{" "}
              {candidate.totalWeight ? `${candidate.totalWeight} кг` : "—"}
            </Box>
            <Box>Кількість місць: {candidate.placesCount ?? "—"}</Box>
          </Stack>
        </Collapse>
      </Stack>

      <Box
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dark-blue",
          { "border-blue": selected },
        )}
      >
        {selected && <Box className="h-2.5 w-2.5 rounded-full bg-blue" />}
      </Box>
    </Box>
  );
};

export const ApiCallsAutoSearchVehicleSelectModal = ({
  candidates,
  plateNumber,
  submitStepId,
  cancelStepId,
  onClose,
}: ApiCallsAutoSearchVehicleSelectModalData) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showValidationError, setShowValidationError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const { productSlug } = useParams<{ productSlug: string }>();
  const { callProductAnalyticEvent } = useAnalytics();
  const { confirmMtsbuVehicle } = useConfirmMtsbuVehicle();
  const { goToStep } = useStepsNavigation();
  const resetFields = useFieldsReset();

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    setShowValidationError(false);
  };

  const handleClose = () => {
    callProductAnalyticEvent(productSlug, { eventName: "vehicle_close" });
    onClose?.();
    apiCallsModalStore.getState().close();
  };

  const handleNotMyCar = async () => {
    callProductAnalyticEvent(productSlug, { eventName: "vehicle_not_mine" });
    setIsRejecting(true);
    try {
      await resetFields(mtsbumvsFields);
      if (cancelStepId) {
        await goToStep(cancelStepId);
      }
    } finally {
      apiCallsModalStore.getState().close();
      setIsRejecting(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedIndex === null) {
      setShowValidationError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmMtsbuVehicle(selectedIndex, cancelStepId);

      callProductAnalyticEvent(productSlug, { eventName: "vehicle_continue" });

      if (submitStepId) {
        await goToStep(submitStepId);
      }
      apiCallsModalStore.getState().close();
    } catch (e) {
      if (!isStopCallsError(e)) {
        throw e;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="flex max-h-[90vh] w-full max-w-[524px] flex-col gap-6 overflow-y-auto rounded-xl bg-white p-6 shadow-xl center-modal max-md:animate-up-modal max-md:bottom-modal">
      <Stack className="w-full flex-center-row">
        <Typography
          variant="h1"
          component="h3"
          className="flex-1 text-center text-[25px] font-semibold leading-8"
        >
          {"Транспортний засіб"}
        </Typography>
        <IconButton onClick={handleClose}>
          <WebShopIcon name="close" className="fill-blue" />
        </IconButton>
      </Stack>

      <Stack className="flex flex-col gap-3">
        {candidates.map((candidate, index) => (
          <VehicleCandidateCard
            key={index}
            candidate={candidate}
            plateNumber={plateNumber}
            selected={selectedIndex === index}
            onSelect={() => handleSelect(index)}
          />
        ))}
      </Stack>

      <Typography
        className={cn(
          "text-left text-sm",
          showValidationError ? "text-red" : "text-dark-gray",
        )}
      >
        Оберіть, будь ласка, транспортний засіб
      </Typography>

      <Stack className="flex flex-row flex-nowrap gap-3 [&>*]:flex-1">
        <Button
          variant={"outlined"}
          onClick={handleNotMyCar}
          loading={isRejecting}
          disabled={isSubmitting}
        >
          це не моє авто
        </Button>
        <Button onClick={handleSubmit} loading={isSubmitting} disabled={isRejecting}>
          Продовжити
        </Button>
      </Stack>
    </Box>
  );
};
